const ADMIN_ROLE_NAMES = new Set([
  'hke_admin',
  'consultant_admin',
  'network_admin',
  'network_manager',
  'organization_admin',
  'org_admin',
  'org_master',
]);

const USER_ROLE_NAMES = new Set([
  'organization_user',
  'org_user',
]);

const normalizeString = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export const normalizeSafetyAssignment = (assignment) => {
  if (!assignment || typeof assignment !== 'object') return null;

  const name = normalizeString(
    assignment.role_name ??
      assignment.role?.name ??
      assignment.name ??
      assignment.role
  );
  const scope = normalizeString(assignment.scope ?? assignment.scope_type ?? assignment.role?.scope);

  if (!name) return null;

  return {
    name,
    scope,
    consultant_id: toPositiveNumber(assignment.consultant_id ?? assignment.consultant?.id),
    network_id: toPositiveNumber(assignment.network_id ?? assignment.network?.id),
    organization_id: toPositiveNumber(
      assignment.organization_id ??
        assignment.organization?.id ??
        assignment.org_id ??
        assignment.org?.id
    ),
    consultant_name: assignment.consultant?.name ?? assignment.consultant_name ?? null,
    network_name: assignment.network?.name ?? assignment.network_name ?? null,
    organization_name:
      assignment.organization?.name ?? assignment.organization_name ?? assignment.org?.name ?? null,
  };
};

export const getSafetyAssignments = (user) => {
  const directAssignments = Array.isArray(user?.rbac?.assignments) ? user.rbac.assignments : [];
  const roleEntries = []
    .concat(Array.isArray(user?.roles) ? user.roles : [])
    .concat(Array.isArray(user?.user_roles) ? user.user_roles : []);

  return directAssignments
    .concat(roleEntries)
    .map((entry) => {
      if (typeof entry === 'string') return { name: normalizeString(entry), scope: '' };
      return normalizeSafetyAssignment(entry);
    })
    .filter(Boolean);
};

const getRoleTier = (roleName) => {
  const normalized = normalizeString(roleName);
  if (ADMIN_ROLE_NAMES.has(normalized)) return 'admin';
  if (USER_ROLE_NAMES.has(normalized)) return 'user';
  return null;
};

const assignmentKey = (assignment) =>
  [
    normalizeString(assignment?.name),
    normalizeString(assignment?.scope),
    toPositiveNumber(assignment?.consultant_id) || '',
    toPositiveNumber(assignment?.network_id) || '',
    toPositiveNumber(assignment?.organization_id) || '',
  ].join('::');

const getManagementScopeLabel = (assignment) => {
  const scope = normalizeString(assignment?.scope);
  if (scope === 'app') return 'platform';
  if (scope === 'consultant') return assignment?.consultant_name || assignment?.consultant?.name || 'this consultant';
  if (scope === 'network') return assignment?.network_name || assignment?.network?.name || 'this network';
  if (scope === 'organization') {
    return assignment?.organization_name || assignment?.organization?.name || 'this school';
  }
  return 'this scope';
};

const getLastAdminMessage = (assignment) => {
  const scope = normalizeString(assignment?.scope);
  if (scope === 'app') return 'This is the last platform admin.';
  if (scope === 'consultant') return `This is the last admin for ${getManagementScopeLabel(assignment)}.`;
  if (scope === 'network') return `This is the last admin for ${getManagementScopeLabel(assignment)}.`;
  if (scope === 'organization') return `This is the last admin for ${getManagementScopeLabel(assignment)}.`;
  return 'This is the last admin in this scope.';
};

const getManagementRemovalMessage = (assignment) =>
  `This action will remove management access for ${getManagementScopeLabel(assignment)}.`;

const findMatchingAdmins = (users, targetAssignment) => {
  const targetKey = assignmentKey(targetAssignment);

  return users.filter((user) =>
    getSafetyAssignments(user).some((assignment) => {
      if (getRoleTier(assignment?.name) !== 'admin') return false;
      return assignmentKey(assignment) === targetKey;
    })
  );
};

const uniqueMessages = (messages) => Array.from(new Set(messages.filter(Boolean)));

export const getDeleteUserSafety = (targetUser, users = []) => {
  if (!targetUser) {
    return {
      isBlocked: false,
      blockReason: '',
      warnings: [],
      confirmationMessage: 'Delete this user?',
    };
  }

  const directBlockReason =
    targetUser?.delete_blocked_reason ??
    targetUser?.delete_disabled_reason ??
    targetUser?.deletion_block_reason ??
    '';
  const directWarnings = []
    .concat(targetUser?.delete_warnings || [])
    .concat(targetUser?.deletion_warnings || [])
    .filter(Boolean);

  const adminAssignments = getSafetyAssignments(targetUser).filter(
    (assignment) => getRoleTier(assignment?.name) === 'admin'
  );
  const inferredWarnings = [];
  let inferredBlockReason = '';

  adminAssignments.forEach((assignment) => {
    const matchingAdmins = findMatchingAdmins(users, assignment);
    if (matchingAdmins.length <= 1 && !inferredBlockReason) {
      inferredBlockReason = getLastAdminMessage(assignment);
      return;
    }

    inferredWarnings.push(getManagementRemovalMessage(assignment));
  });

  const warnings = uniqueMessages([...directWarnings, ...inferredWarnings]);
  const blockReason = directBlockReason || inferredBlockReason;
  const summaryLines = [];

  if (blockReason) summaryLines.push(blockReason);
  summaryLines.push(...warnings);
  summaryLines.push('Delete this user?');

  return {
    isBlocked: Boolean(blockReason),
    blockReason,
    warnings,
    confirmationMessage: summaryLines.join(' '),
  };
};

export const getRoleChangeSummary = (originalRoles = [], nextRoles = []) => {
  const originalByKey = new Map(originalRoles.map((role) => [assignmentKey(role), role]));
  const nextByKey = new Map(nextRoles.map((role) => [assignmentKey(role), role]));

  const added = nextRoles.filter((role) => !originalByKey.has(assignmentKey(role)));
  const removed = originalRoles.filter((role) => !nextByKey.has(assignmentKey(role)));

  const originalByScopeContext = new Map();
  const nextByScopeContext = new Map();

  const scopeContextKey = (role) =>
    [
      normalizeString(role?.scope),
      toPositiveNumber(role?.consultant_id) || '',
      toPositiveNumber(role?.network_id) || '',
      toPositiveNumber(role?.organization_id) || '',
    ].join('::');

  originalRoles.forEach((role) => originalByScopeContext.set(scopeContextKey(role), role));
  nextRoles.forEach((role) => nextByScopeContext.set(scopeContextKey(role), role));

  const downgraded = Array.from(originalByScopeContext.entries())
    .map(([key, originalRole]) => {
      const nextRole = nextByScopeContext.get(key);
      if (!nextRole) return null;

      const originalTier = getRoleTier(originalRole?.name);
      const nextTier = getRoleTier(nextRole?.name);
      if (originalTier !== 'admin' || nextTier !== 'user') return null;

      return {
        from: originalRole,
        to: nextRole,
      };
    })
    .filter(Boolean);

  const warnings = uniqueMessages(
    downgraded.map(({ from }) => getManagementRemovalMessage(from))
  );

  const summaryItems = [];
  if (added.length > 0) summaryItems.push(`${added.length} role${added.length === 1 ? '' : 's'} added`);
  if (removed.length > 0) summaryItems.push(`${removed.length} role${removed.length === 1 ? '' : 's'} removed`);
  if (downgraded.length > 0) summaryItems.push(`${downgraded.length} downgrade${downgraded.length === 1 ? '' : 's'}`);

  return {
    added,
    removed,
    downgraded,
    warnings,
    summaryLabel: summaryItems.join(', '),
  };
};
